import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import type { FormNode, TableColumn } from '../../../../types/form.types'
import { useFormChrome } from '../formChromeTheme'
import { MONO_FONT } from '@/shared/typography'
import { TabularSectionTitle } from '@/shared/components/TabularSectionTitle'

export interface TableElementProps {
  node: FormNode
  preview?: boolean
  selectedColumnId?: string | null
  onColumnHeaderClick?: (columnId: string, event: React.MouseEvent) => void
}

/** The one header cell for both table chromes. On the Designer canvas it is clickable. */
function ColumnHeaderCell({
  col,
  journal,
  selected,
  interactive,
  onColumnHeaderClick,
}: {
  col: TableColumn
  journal: boolean
  selected: boolean
  interactive: boolean
  onColumnHeaderClick?: (columnId: string, event: React.MouseEvent) => void
}) {
  const chrome = useFormChrome()
  return (
    <TableCell
      aria-selected={selected}
      align={col.align === 'right' ? 'right' : 'left'}
      onClick={interactive ? (event) => {
        event.stopPropagation()
        onColumnHeaderClick?.(col.id, event)
      } : undefined}
      onContextMenu={interactive ? (event) => {
        event.preventDefault()
        event.stopPropagation()
      } : undefined}
      sx={{
        pointerEvents: interactive ? 'auto' : undefined,
        cursor: interactive ? 'pointer' : undefined,
        fontSize: journal ? undefined : 12,
        fontWeight: journal ? undefined : 600,
        color: col.visible === false ? 'text.disabled' : journal ? undefined : 'text.secondary',
        py: journal ? undefined : 0.5,
        whiteSpace: journal ? undefined : 'nowrap',
        width: !journal && col.width !== 'auto' ? col.width : undefined,
        borderStyle: selected ? 'solid' : undefined,
        borderWidth: selected ? 1 : undefined,
        borderColor: selected ? chrome.selectionRing : undefined,
        ...(col.minWidth ? { minWidth: col.minWidth } : {}),
        ...(col.maxWidth ? { maxWidth: col.maxWidth } : {}),
      }}
    >
      {col.title}
      {!journal && col.sortable && (
        <Typography component="span" sx={{ fontSize: 10, ml: 0.5, color: 'text.disabled' }}>
          ↕
        </Typography>
      )}
    </TableCell>
  )
}

export function TableElement({ node, preview, selectedColumnId = null, onColumnHeaderClick }: TableElementProps) {
  const chrome = useFormChrome()
  const canvas = preview === false
  const columns = (node.columns ?? []).filter((column) => canvas || column.visible !== false)
  const journal = node.style?.tableChrome === 'journal'
  const staticRows = node.staticPreviewRows ?? []
  const showToolbar = node.showToolbar !== false
  const showRowNumbers = !!node.showRowNumbers
  const allowAdd = node.allowAdd !== false
  const allowDelete = node.allowDelete !== false
  const allowReorder = !!node.allowReorder
  const emptyMessage = node.emptyMessage || 'No data'
  const rowHeight = node.rowHeight

  if (journal) {
    return (
      <Box sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table
            size="small"
            sx={{
              '& th': {
                fontFamily: MONO_FONT,
                fontSize: 10,
                fontWeight: 500,
                color: chrome.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                py: 1,
                px: 1.75,
                borderBottom: `1px solid ${chrome.cardBorder}`,
                bgcolor: chrome.headerBg,
              },
              '& td': {
                py: rowHeight ? `${rowHeight / 2}px` : 1.25,
                px: 1.75,
                fontSize: 12,
                borderBottom: `1px solid ${chrome.cardBorder}`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {showRowNumbers && <TableCell sx={{ width: 32 }}>#</TableCell>}
                {columns.map((col) => (
                  <ColumnHeaderCell
                    key={col.id}
                    col={col}
                    journal={journal}
                    selected={selectedColumnId === col.id}
                    interactive={canvas}
                    onColumnHeaderClick={onColumnHeaderClick}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {staticRows.length === 0 ? (
                <TableRow>
                  {showRowNumbers && <TableCell />}
                  {columns.map((col: TableColumn) => (
                    <TableCell key={col.id} sx={{ color: 'text.disabled' }}>
                      &nbsp;
                    </TableCell>
                  ))}
                </TableRow>
              ) : (
                staticRows.map((raw, ri) => {
                  const row = raw as Record<string, string> & { __total?: boolean }
                  const isTotal = !!row.__total
                  return (
                    <TableRow
                      key={ri}
                      sx={
                        isTotal
                          ? {
                              '& td': {
                                fontWeight: 600,
                                bgcolor: chrome.headerBg,
                                borderTop: `2px solid ${chrome.outlineHover}`,
                              },
                            }
                          : undefined
                      }
                    >
                      {showRowNumbers && (
                        <TableCell sx={{ color: 'text.disabled', fontSize: 10 }}>
                          {isTotal ? '' : row.num !== undefined && row.num !== '' ? row.num : ri + 1}
                        </TableCell>
                      )}
                      {columns.map((col: TableColumn) => {
                        const v = String(row[col.name] ?? '')
                        const debitCol = col.name === 'debit'
                        const creditCol = col.name === 'credit'
                        return (
                          <TableCell
                            key={col.id}
                            align={col.align === 'right' ? 'right' : 'left'}
                            sx={{
                              fontFamily:
                                debitCol || creditCol || (isTotal && col.name === 'account')
                                  ? MONO_FONT
                                  : undefined,
                              color: debitCol ? chrome.debit : creditCol ? chrome.credit : undefined,
                              ...(isTotal && col.name === 'account'
                                ? {
                                    color: chrome.credit,
                                    fontSize: 11,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }
                                : {}),
                            }}
                          >
                            {v}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  const sectionTitle =
    node.titleVisible !== false && node.title ? <TabularSectionTitle title={node.title} /> : null

  const content = (
    <Box>
      {sectionTitle}
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      {showToolbar && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.5,
            py: 0.25,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          {allowAdd && (
            <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
              <AddIcon fontSize="small" />
            </IconButton>
          )}
          {allowDelete && (
            <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
          {allowReorder && (
            <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
      <TableContainer>
        <Table size="small" sx={{ pointerEvents: 'none' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              {showRowNumbers && (
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', py: 0.5, width: 32 }}>
                  #
                </TableCell>
              )}
              {columns.map((col) => (
                <ColumnHeaderCell
                  key={col.id}
                  col={col}
                  journal={journal}
                  selected={selectedColumnId === col.id}
                  interactive={canvas}
                  onColumnHeaderClick={onColumnHeaderClick}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={rowHeight ? { '& td': { height: rowHeight } } : undefined}>
              {showRowNumbers && (
                <TableCell sx={{ fontSize: 11, py: 0.5, color: 'text.disabled' }}>1</TableCell>
              )}
              {columns.map((col: TableColumn) => (
                <TableCell
                  key={col.id}
                  align={col.align === 'right' ? 'right' : 'left'}
                  sx={{
                    fontSize: 12,
                    py: 0.5,
                    color: 'text.disabled',
                    ...(col.mono ? { fontFamily: MONO_FONT } : {}),
                  }}
                >
                  {col.chip ? (
                    <Chip label="Sample" size="small" sx={{ fontSize: 11, height: 22 }} />
                  ) : col.editable ? (
                    <Box sx={{ borderBottom: '1px dashed', borderColor: 'divider', display: 'inline', pb: 0.25 }}>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    </Box>
                  ) : (
                    '\u00A0'
                  )}
                </TableCell>
              ))}
            </TableRow>
            {columns.length === 0 && (
              <TableRow>
                <TableCell colSpan={99} sx={{ textAlign: 'center', color: 'text.disabled', py: 2 }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
    </Box>
  )

  if (node.tooltip) {
    return <Tooltip title={node.tooltip}>{content}</Tooltip>
  }

  return content
}
